import React, { useState, useEffect } from "react";
import { Button, Form, Card, Row, Col } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import "../../../assets/css/Variant/AddVariantValue.css";

const API_BASE_URL = process.env.REACT_APP_API_URL;

const AddVariantValue = () => {
    const { id } = useParams();
    const [variantList, setVariantList] = useState([]);
    const [formData, setFormData] = useState({
        variantName: "",
        values: [""], // ARRAY OF VALUES
        remarks: "",
        status: true,
    });

    const navigate = useNavigate();
    const token = localStorage.getItem("token");

    useEffect(() => {
        const fetchVariants = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/api/variant-names`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setVariantList(res.data);
            } catch (error) {
                console.error("Error fetching variant names:", error);
                if (error.response?.status === 401) {
                    alert("Unauthorized. Please login again.");
                    navigate("/login");
                }
            }
        };
        fetchVariants();
    }, [token, navigate]);

    useEffect(() => {
        if (!id) return;

        const fetchVariantValue = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/api/variant-values/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const { variantName, values, remarks, status } = res.data;
                setFormData({
                    variantName: variantName?._id || "",
                    values: values?.length ? values : [""],
                    remarks: remarks || "",
                    status,
                });
            } catch (error) {
                console.error("Error fetching variant value:", error);
                alert("Failed to load variant value data.");
                navigate("/variant-values");
            }
        };
        fetchVariantValue();
    }, [id, token, navigate]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
    };

    const handleValueChange = (index, value) => {
        const updatedValues = [...formData.values];
        updatedValues[index] = value;
        setFormData({ ...formData, values: updatedValues });
    };

    const addValueField = () => {
        setFormData({ ...formData, values: [...formData.values, ""] });
    };

    const removeValueField = (index) => {
        const updatedValues = [...formData.values];
        updatedValues.splice(index, 1);
        setFormData({ ...formData, values: updatedValues });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (id) {
                await axios.put(`${API_BASE_URL}/api/variant-values/${id}`, formData, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            } else {
                await axios.post(`${API_BASE_URL}/api/variant-values`, formData, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            }
            navigate("/variant-values");
        } catch (error) {
            console.error("Error saving variant value:", error.response?.data || error.message);
            alert("Failed to save variant value.");
        }
    };

    return (
        <div className="container py-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="form-title1">{id ? "Edit Variant Value" : "Add Variant Value"}</h4>
                <Button variant="outline-primary" onClick={() => navigate("/variant-values")} className="back-btn">
                    ← Back to Variant Values
                </Button>
            </div>

            <Card className="form-card">
                <Card.Header className="form-card-header">
                    Variant Value {id ? "Edit" : "Creation"} Form
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={handleSubmit}>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group controlId="variantName">
                                    <Form.Label>Variant Name *</Form.Label>
                                    <Form.Select
                                        name="variantName"
                                        value={formData.variantName}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="">Select Variant Name</option>
                                        {variantList.map((v) => (
                                            <option key={v._id} value={v._id}>
                                                {v.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={12}>
                                <Form.Label>Values *</Form.Label>
                                {formData.values.map((val, idx) => (
                                    <div key={idx} className="d-flex mb-2">
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter Value"
                                            value={val}
                                            onChange={(e) => handleValueChange(idx, e.target.value)}
                                            required
                                        />
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            className="ms-2"
                                            onClick={() => removeValueField(idx)}
                                            disabled={formData.values.length === 1}
                                        >
                                            ✖
                                        </Button>
                                    </div>
                                ))}
                                <Button variant="success" size="sm" onClick={addValueField}>
                                    + Add More
                                </Button>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group controlId="remarks">
                                    <Form.Label>Remarks</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter Remarks"
                                        name="remarks"
                                        value={formData.remarks}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={6} className="d-flex align-items-center">
                                <Form.Group controlId="status" className="w-100">
                                    <Form.Check
                                        type="switch"
                                        className="custom-switch"
                                        label={
                                            <span style={{ color: formData.status ? "green" : "red" }}>
                                                {formData.status ? "Active" : "Inactive"}
                                            </span>
                                        }
                                        name="status"
                                        checked={formData.status}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end gap-2">
                            <Button type="button" variant="secondary" onClick={() => navigate("/variant-values")}>
                                Cancel
                            </Button>
                            <Button type="submit" variant="primary" className="submit-btn">
                                {id ? "Update" : "Save"}
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AddVariantValue;
